// ENG-5730: aligned with the DB CHECK constraint on agent_kanban_items.status
// (migration 20260530000003). Previously this union omitted 'cancelled' and
// 'needs_attention' — both are valid persisted states (agents reach 'cancelled'
// via kanban_cancel; the stale-item reaper writes 'needs_attention'), so the
// type silently disagreed with the database. The state machine in
// `kanban/state-machine.ts` keys off this full set.
export type KanbanStatus =
  | 'backlog'
  | 'todo'
  | 'in_progress'
  | 'done'
  | 'failed'
  | 'cancelled'
  | 'needs_attention';
export type KanbanSource = 'cron' | 'chat' | 'manual' | 'integration';

export interface KanbanItem {
  id: string;
  agent_id: string;
  team_id: string;
  title: string;
  description?: string;
  priority: number;          // 1=high, 2=medium, 3=low
  status: KanbanStatus;
  estimated_minutes?: number;
  notes?: string;
  source: KanbanSource;
  source_integration?: string;   // e.g., 'linear', 'github'
  source_external_id?: string;   // ID in the external system
  source_url?: string;           // deep link to external source
  last_synced_at?: string;       // ISO timestamp of last upstream refresh (ENG-4604)
  deliverable?: string;
  result?: string;
  notify_channel?: string;
  notify_to?: string;
  /**
   * ADR-0017: optional link to a `projects` container. Nullable; tagging is
   * Phase 2 (an optional arg on kanban_create), so most rows carry null.
   */
  project_id?: string | null;
  started_at?: string;
  completed_at?: string;
  /**
   * ENG-4507: actor that last set this row's status. Subscribers filter on
   * this to suppress agent self-completion round-trips.
   *
   * Format:
   *   - "agent:<agent_id>" — set by MCP write paths (kanban_done, kanban_update)
   *   - "user:<user_id>"   — set by webapp PATCH from the console kanban board
   *   - undefined / null   — legacy rows, treated as "unknown actor" (deliver)
   */
  last_actor_id?: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * ENG-4507: realtime kanban completion event surfaced via Supabase Realtime
 * `postgres_changes` on `agent_kanban_items`. Subscribers (the manager
 * daemon, integration tests) derive this from the row diff — there is no
 * separate emitted schema. Centralised here so every reader uses the same
 * shape and the contract can evolve in one place.
 */
export interface KanbanCompletionEvent {
  agent_id: string;
  item_id: string;
  status: 'done' | 'failed';
  last_actor_id: string | null;
  completed_at: string | null;
  title: string;
}

/**
 * Build the canonical `last_actor_id` value for a status write. Keeping the
 * formatting in one helper means MCP and webapp paths can't drift on the
 * "agent:" / "user:" prefix convention.
 */
export function formatActorId(kind: 'agent' | 'user', id: string): string {
  return `${kind}:${id}`;
}

/**
 * True when an agent should ignore a completion event because it was that
 * same agent that closed the row. Subscribers call this to short-circuit
 * before forwarding the notification into the runtime.
 */
export function isSelfCompletion(event: KanbanCompletionEvent): boolean {
  return event.last_actor_id === formatActorId('agent', event.agent_id);
}

/**
 * ENG-4515: classify the actor on a kanban row from the perspective of a
 * specific agent. Used by `kanban_list` to surface a `closed_by` annotation
 * so the agent can tell user-driven closures apart from its own and stop
 * redoing work the user has already handled.
 *
 * Returns:
 *   - 'self'    — this agent closed the row (suppress redo logic — but the agent
 *                 already knows; the annotation is just informational)
 *   - 'user'    — a human closed the row from the console; the work is done
 *   - 'other'   — a different agent on the team closed it
 *   - 'unknown' — legacy row with no actor recorded; assume external closure
 */
export function classifyActor(
  lastActorId: string | null | undefined,
  selfAgentId: string,
): 'self' | 'user' | 'other' | 'unknown' {
  if (!lastActorId) return 'unknown';
  if (lastActorId === formatActorId('agent', selfAgentId)) return 'self';
  if (lastActorId.startsWith('user:')) return 'user';
  if (lastActorId.startsWith('agent:')) return 'other';
  return 'unknown';
}
